import React, { useMemo } from "react";
import { UNSELECTED_OPTION_VALUE, FIELD_TYPE } from "@fieldEngine";
import {
  ACTIVITY_FIELD_KEY,
  ACTIVITY_FIELD_LABEL,
  OWNER_PLACEHOLDER,
} from "../constants";
import MakeMeOwnerButton from '../../AddEditWorkkard/components/MakeMeOwnerButton';

function buildOwnerOptions(users, currentUserId, currentUser) {
  const seen = new Set();
  const opts = users.reduce((acc, u) => {
    const id = String(u.id);
    if (seen.has(id)) return acc;
    seen.add(id);
    acc.push({
      key: id,
      value:
        u.displayName ??
        `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ??
        id,
    });
    return acc;
  }, []);

  if (currentUserId && !opts.some((o) => o.key === currentUserId)) {
    const selfLabel =
      `${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`.trim() ||
      currentUser?.username ||
      "Me";
    opts.unshift({ key: currentUserId, value: selfLabel });
  }

  if (opts.length === 0) return [];
  return [{ key: UNSELECTED_OPTION_VALUE, value: OWNER_PLACEHOLDER }, ...opts];
}

export function useStaticFieldEnrichments({
  users,
  currentUserId,
  currentUser,
  isEdit = false,
  defaultValue
}) {
  return useMemo(
    () => ({
      "activity title": {
        fieldKey: ACTIVITY_FIELD_KEY.TITLE,
        fieldType: FIELD_TYPE.STRING,
        label: ACTIVITY_FIELD_LABEL.TITLE,
        isRequired: true,
        requiredMessage: "Activity Title is required.",
        description:
          "Provide a one liner about this activity that you can use as a reference.",
        items: [],
        fieldSettings: {
          colWidth: 9,
        }
      },
      "activity due date": {
        fieldKey: ACTIVITY_FIELD_KEY.DUE_DATE,
        fieldType: FIELD_TYPE.DATE_WITH_TIMEZONE,
        label: ACTIVITY_FIELD_LABEL.DUE_DATE,
        isRequired: true,
        requiredMessage: "Due Date is required.",
        description: "",
        items: [],
        fieldSettings: {
          optionsFromStore: "sessionContextOidc.tzData",
          ...(!isEdit && { path: 'sessionContextOidc.userView.timezone' }),
          ...(isEdit && { defaultValue }),
          colWidth: 9,
        },
      },
      "activity owner": {
        fieldKey: ACTIVITY_FIELD_KEY.OWNER,
        fieldType: FIELD_TYPE.SINGLE_CHOICE_LIST,
        label: ACTIVITY_FIELD_LABEL.OWNER,
        isRequired: true,
        requiredMessage: "Activity Owner is required.",
        description: "",
        items: buildOwnerOptions(users, currentUserId, currentUser),
        fieldSettings: currentUserId && !isEdit
          ? {
            rightAdornment: ({ value, onChange, disabled }) => (
              <MakeMeOwnerButton
                value={value}
                onChange={onChange}
                disabled={disabled}
                currentUserId={currentUserId}
              />
            ),
          }
          : undefined,
          colWidth: 9,
      },
      "activity description": {
        fieldKey: ACTIVITY_FIELD_KEY.DESCRIPTION,
        fieldType: FIELD_TYPE.RICH_TEXT,
        label: ACTIVITY_FIELD_LABEL.DESCRIPTION,
        isRequired: false,
        description:
          "Write a few lines about this activity. Please make sure it provides good insight about the activity for your vendors or for future reference. You can edit this later. You can use wiki markup to decorate or editor buttons to make the content look right. Read about wiki markup, if you have not done so already.",
        items: [],
        fieldSettings: {
          colWidth: 9,
        }
      },
      "choose a skillset": {
        fieldKey: ACTIVITY_FIELD_KEY.SKILLSET,
        fieldType: FIELD_TYPE.TAG,
        label: ACTIVITY_FIELD_LABEL.SKILLSET,
        isRequired: true,
        requiredMessage: "Skillset is required.",
        description:
          "You can only choose one skillset per Activity. If you have multiple skillsets involved for this activity, please create a separate Activity for each skillset",
        items: [],
        fieldSettings: {
          optionsFromStore: "activitySkillsetTags.tags",
          maxTags: 1,
          maxTagsError: "Maximum 1 Skillset is allowed.",
          placeholder: "Search skillset",
          allowCustomTags: true,
          colWidth: 9,
        },
      },
      "choose an assignment method": {
        fieldKey: ACTIVITY_FIELD_KEY.ASSIGNMENT,
        fieldType: FIELD_TYPE.ASSIGNMENT_METHOD,
        label: ACTIVITY_FIELD_LABEL.ASSIGNMENT,
        isRequired: true,
        requiredMessage: "Assignee is required.",
        description: "",
        items: [],
        fieldSettings: {
          ...(isEdit && { isEdit }),
          colWidth: 9,
        },
      },
    }),
    [users, currentUserId, currentUser, isEdit, defaultValue],
  );
}